import React, { useState } from 'react';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG';
import Logo from '../../../assetes/logos.png';

const FormulaireCandidature = ({ offreId, titre, onSuccess }) => {
  // ---------- ÉTATS ----------
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    sexe: '',
    email: '',
    tel: '',
    adresse: '',
    competence: '',
    niveua_etutde: '',
    experience: '',
    langues: '',
  });

  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // ---------- GESTIONNAIRES ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (file.size > maxSize) {
        setError('Le fichier ne doit pas dépasser 5 Mo.');
        e.target.value = '';
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        setError('Seuls les fichiers PDF, DOC ou DOCX sont acceptés.');
        e.target.value = '';
        return;
      }
      setCvFile(file);
      setCvFileName(file.name);
      setError(null);
    } else {
      setCvFile(null);
      setCvFileName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!offreId) {
      setError("L'ID de l'offre est manquant.");
      setLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });
    formDataToSend.append('offreId', offreId);
    if (cvFile) {
      formDataToSend.append('cv', cvFile);
    }

    try {
      const response = await fetch(`${API_CONFIG2.apiUrl}offres/postuler.php`, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setSuccess(true);
        if (onSuccess) onSuccess(result);
        setFormData({
          nom: '',
          prenom: '',
          date_naissance: '',
          sexe: '',
          email: '',
          tel: '',
          adresse: '',
          competence: '',
          niveua_etutde: '',
          experience: '',
          langues: '',
        });
        setCvFile(null);
        setCvFileName('');
        document.getElementById('cv-upload').value = '';
      } else {
        setError(result.message || 'Une erreur est survenue.');
      }
    } catch (err) {
      setError('Erreur réseau : ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- RENDU ----------
  return (
    <>
      <style>{`
        /* ==========================================================
           DARK BLUE GLASSMORPHISM – FORMULAIRE CANDIDATURE
           ========================================================== */
        :root {
          --bg-primary: #0b1428;
          --glass-bg: rgba(12, 28, 58, 0.55);
          --glass-border: rgba(255,255,255,0.08);
          --glass-shadow: 0 8px 32px rgba(0,0,0,0.5);
          --text-primary: #f0f4ff;
          --text-secondary: #9bb0d9;
          --text-muted: #6a80aa;
          --accent-blue: #4a8aff;
          --accent-cyan: #00d4ff;
          --accent-purple: #8b5cf6;
          --radius: 20px;
          --transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        * { box-sizing: border-box; }

        .candidature-page {
          min-height: 100vh;
          padding: 40px 24px 60px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: var(--font-family);
          background: var(--bg-primary);
          background-image: radial-gradient(ellipse at 10% 20%, rgba(74,138,255,0.12) 0%, transparent 60%),
                            radial-gradient(ellipse at 90% 80%, rgba(139,92,246,0.08) 0%, transparent 50%);
          position: relative;
        }

        .candidature-form-container {
          max-width: 860px;
          width: 100%;
          background: var(--glass-bg);
          backdrop-filter: blur(20px) saturate(1.4);
          -webkit-backdrop-filter: blur(20px) saturate(1.4);
          border: 1px solid var(--glass-border);
          border-radius: var(--radius);
          padding: 48px 48px 40px;
          box-shadow: var(--glass-shadow);
          transition: box-shadow var(--transition), transform var(--transition);
          animation: fadeSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .candidature-form-container:hover {
          box-shadow: 0 16px 48px rgba(0,0,0,0.6);
          border-color: rgba(74,138,255,0.2);
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .form-header {
          display: flex;
          align-items: center;
          gap: 24px;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .candidature-logo {
          width: 64px;
          height: auto;
          flex-shrink: 0;
          filter: drop-shadow(0 4px 12px rgba(74,138,255,0.3));
        }

        .form-header-content {
          flex: 1;
        }

        .candidature-form-container h2 {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #f0f4ff, #9bb0d9);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .candidature-form-container h2 span {
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-cyan));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .subtitle {
          color: var(--text-secondary);
          font-size: 0.95rem;
          margin: 4px 0 0;
          font-weight: 400;
        }

        .subtitle strong {
          color: var(--text-primary);
          font-weight: 600;
        }

        .success-message,
        .error-message {
          padding: 14px 20px;
          border-radius: 12px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.95rem;
          font-weight: 500;
          animation: fadeSlide 0.3s ease;
          backdrop-filter: blur(8px);
        }

        .success-message {
          background: rgba(46,204,113,0.12);
          border: 1px solid rgba(46,204,113,0.2);
          color: #6fcf97;
        }

        .error-message {
          background: rgba(239,68,68,0.12);
          border: 1px solid rgba(239,68,68,0.2);
          color: #f87171;
        }

        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .candidature-form {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 6px;
          letter-spacing: 0.01em;
        }

        .form-group label .required {
          color: #f87171;
          font-weight: 700;
          margin-left: 2px;
        }

        .form-group label .field-icon {
          font-size: 1rem;
          opacity: 0.7;
          width: 20px;
          text-align: center;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.04);
          border: 1.5px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          font-size: 0.95rem;
          font-family: var(--font-family);
          color: var(--text-primary);
          transition: var(--transition);
          outline: none;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: var(--text-muted);
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          border-color: var(--accent-blue);
          background: rgba(74,138,255,0.05);
          box-shadow: 0 0 0 3px rgba(74,138,255,0.15);
        }

        .form-group input:hover,
        .form-group select:hover,
        .form-group textarea:hover {
          border-color: rgba(74,138,255,0.3);
        }

        .form-group select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239bb0d9' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          padding-right: 40px;
        }

        .form-group select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .cv-upload-wrapper {
          position: relative;
          border: 2px dashed rgba(255,255,255,0.08);
          border-radius: 16px;
          padding: 32px 20px;
          text-align: center;
          transition: var(--transition);
          background: rgba(255,255,255,0.02);
          cursor: pointer;
        }

        .cv-upload-wrapper:hover {
          border-color: var(--accent-blue);
          background: rgba(74,138,255,0.04);
        }

        .cv-upload-wrapper.has-file {
          border-style: solid;
          border-color: var(--accent-blue);
          background: rgba(74,138,255,0.06);
        }

        .cv-upload-wrapper input[type="file"] {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .cv-upload-icon {
          font-size: 2.8rem;
          display: block;
          margin-bottom: 8px;
          opacity: 0.6;
        }

        .cv-upload-text {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }

        .cv-upload-text strong {
          color: var(--text-primary);
          font-weight: 600;
        }

        .cv-upload-text small {
          display: block;
          margin-top: 4px;
          color: var(--text-muted);
          font-size: 0.8rem;
        }

        .cv-file-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 12px;
          padding: 8px 16px;
          background: rgba(74,138,255,0.08);
          border-radius: 8px;
          color: var(--accent-blue);
          font-weight: 500;
        }

        .cv-file-info .remove-file {
          background: none;
          border: none;
          color: #f87171;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          transition: transform 0.2s;
        }

        .cv-file-info .remove-file:hover {
          transform: scale(1.2);
        }

        .btn-submit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 14px 40px;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-cyan));
          color: #ffffff;
          font-weight: 700;
          font-size: 1rem;
          border: none;
          border-radius: 60px;
          cursor: pointer;
          box-shadow: 0 4px 24px rgba(74,138,255,0.3);
          transition: var(--transition);
          margin-top: 8px;
          align-self: flex-start;
          position: relative;
          overflow: hidden;
        }

        .btn-submit::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.5s;
          transform: scale(0.5);
        }

        .btn-submit:hover:not(:disabled)::after {
          opacity: 1;
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 8px 32px rgba(74,138,255,0.4);
        }

        .btn-submit:active:not(:disabled) {
          transform: scale(0.97);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-submit .spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .candidature-form-container {
            padding: 28px 20px;
          }
          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }
          .candidature-form-container h2 {
            font-size: 1.5rem;
          }
          .btn-submit {
            width: 100%;
            justify-content: center;
          }
          .form-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .candidature-logo {
            width: 56px;
          }
        }

        @media (max-width: 480px) {
          .candidature-page {
            padding: 16px 8px;
          }
          .candidature-form-container {
            padding: 20px 14px;
            border-radius: 18px;
          }
        }
      `}</style>

      <div className="candidature-page ">
        <div className="candidature-form-container mt-5">
          <div className="form-header">
            <img src={Logo} alt="Logo" className="candidature-logo" />
            <div className="form-header-content">
              <h2>Postuler à <span>cette offre</span></h2>
              <p className="subtitle">
                {titre ? (
                  <>Offre : <strong>{titre}</strong></>
                ) : (
                  'Remplissez le formulaire ci-dessous pour candidater.'
                )}
              </p>
            </div>
          </div>

          {success && (
            <div className="success-message">
              <span style={{ fontSize: '1.3rem' }}>✅</span>
              Votre candidature a été envoyée avec succès !
            </div>
          )}

          {error && (
            <div className="error-message">
              <span style={{ fontSize: '1.3rem' }}>⚠️</span>
              {error}
            </div>
          )}

          <form className="candidature-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <span className="field-icon">👤</span> Nom <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  placeholder="Votre nom"
                />
              </div>
              <div className="form-group">
                <label>
                  <span className="field-icon">👤</span> Prénom <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  placeholder="Votre prénom"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <span className="field-icon">📅</span> Date de naissance <span className="required">*</span>
                </label>
                <input
                  type="date"
                  name="date_naissance"
                  value={formData.date_naissance}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <span className="field-icon">⚧️</span> Sexe <span className="required">*</span>
                </label>
                <select name="sexe" value={formData.sexe} onChange={handleChange} required>
                  <option value="">Sélectionnez</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <span className="field-icon">✉️</span> Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="exemple@email.com"
                />
              </div>
              <div className="form-group">
                <label>
                  <span className="field-icon">📞</span> Téléphone <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  name="tel"
                  value={formData.tel}
                  onChange={handleChange}
                  required
                  placeholder="+243 812 345 678"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>
                <span className="field-icon">🏠</span> Adresse <span className="required">*</span>
              </label>
              <input
                type="text"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                required
                placeholder="Votre adresse complète"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <span className="field-icon">🎓</span> Niveau d'études <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="niveua_etutde"
                  value={formData.niveua_etutde}
                  onChange={handleChange}
                  placeholder="Ex: Bac+3"
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <span className="field-icon">💼</span> Expérience <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="Ex: 3 ans en gestion"
                  required
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>
                <span className="field-icon">🗣️</span> Langues <span className="required">*</span>
              </label>
              <input
                type="text"
                name="langues"
                value={formData.langues}
                onChange={handleChange}
                placeholder="Français, Anglais, ..."
                required
              />
            </div>

            <div className="form-group full-width">
              <label>
                <span className="field-icon">🧠</span> Compétences
              </label>
              <textarea
                name="competence"
                value={formData.competence}
                onChange={handleChange}
                placeholder="Listez vos compétences clés"
                rows="3"
              />
            </div>

            <div className="form-group full-width">
              <label>
                <span className="field-icon">📄</span> CV (PDF, DOC, DOCX) <span className="required">*</span>
              </label>
              <div className={`cv-upload-wrapper ${cvFile ? 'has-file' : ''}`}>
                <input
                  id="cv-upload"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                  required={!cvFile}
                />
                {!cvFile ? (
                  <>
                    <span className="cv-upload-icon">📤</span>
                    <div className="cv-upload-text">
                      <strong>Cliquez</strong> ou glissez-déposez votre CV ici
                      <small>PDF, DOC, DOCX (max 5 Mo)</small>
                    </div>
                  </>
                ) : (
                  <div className="cv-file-info">
                    <span>📎</span>
                    <span>{cvFileName}</span>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={(e) => {
                        e.preventDefault();
                        setCvFile(null);
                        setCvFileName('');
                        document.getElementById('cv-upload').value = '';
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span> Envoi en cours...
                </>
              ) : (
                <>
                  📨 Postuler
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default FormulaireCandidature;